import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { useStaffLog } from '../utils/useStaffLog';

/* ══════════════════════════════════════════════════════════════════
   CONSTANTS
   ══════════════════════════════════════════════════════════════════ */
const STAGE_SUGGESTIONS = [
  'Design approved', 'Design revision', 'File sent by customer',
  'Bleed marks added', 'Print', 'Test print approved',
  'Lamination', 'Gloss lamination', 'Matte lamination',
  'Soft touch lamination', 'Spot UV',
  'Cutting', 'Die-cut', 'Guillotine cut',
  'Binding', 'Perfect binding', 'Spiral binding', 'Saddle stitch',
  'Quality check', 'Packing', 'Eyelet fitting',
  'On hold — awaiting payment',
];

/* ══════════════════════════════════════════════════════════════════
   HELPERS
   ══════════════════════════════════════════════════════════════════ */
const safeArray = (v) => (Array.isArray(v) ? v : []);

const readyQtyForLine = (readyEntries, lineId) =>
  safeArray(readyEntries)
    .filter((e) => e.itemLineId === lineId)
    .reduce((s, e) => s + (Number(e.qty) || 0), 0);

/* ══════════════════════════════════════════════════════════════════
   STEP CIRCLE
   ══════════════════════════════════════════════════════════════════ */
const StepCircle = ({
  index, done, active, label,
  isOutsourced, vendorName, purchaseNumber,
  onClick, clickable, onViewPurchase,
}) => {
  const [tip, setTip] = useState(false);
  const tipRef        = useRef(null);

  // Close tooltip on outside click
  useEffect(() => {
    if (!tip) return;
    const h = (e) => { if (tipRef.current && !tipRef.current.contains(e.target)) setTip(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [tip]);

  return (
    <div className="pps-step" style={{ position: 'relative' }}>
      <div
        className={[
          'pps-circle',
          done      ? 'pps-circle--done'      : '',
          active    ? 'pps-circle--active'    : '',
          clickable && !done ? 'pps-circle--clickable' : '',
        ].join(' ')}
        onClick={() => { if (clickable && onClick) onClick(); }}
        style={{ cursor: clickable ? 'pointer' : 'default' }}
      >
        {done
          ? <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><polyline points="2,7 6,11 12,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          : <span>{index + 1}</span>
        }
      </div>

      <div className={`pps-label ${done ? 'pps-label--done' : ''} ${active ? 'pps-label--active' : ''}`}>
        {label}
      </div>

      {/* Outsource badge — clickable to show tooltip */}
      {isOutsourced && (
        <div
          ref={tipRef}
          onClick={(e) => { e.stopPropagation(); setTip((t) => !t); }}
          style={{
            fontSize: 9, fontWeight: 700, color: '#7c3aed',
            background: '#faf5ff', border: '1px solid #ddd6fe',
            borderRadius: 99, padding: '1px 6px', marginTop: 2,
            whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
          }}
        >
          🔗 {vendorName ? vendorName.split(' ')[0] : 'Outsourced'}
        </div>
      )}

      {/* Tooltip */}
      {tip && isOutsourced && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%',
          transform: 'translateX(-50%)',
          background: '#1f2937', color: '#fff', fontSize: 12, borderRadius: 10,
          padding: '10px 14px', whiteSpace: 'nowrap', zIndex: 200,
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)', lineHeight: 1.6,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>🔗 Outsourced</div>
          {vendorName     && <div>Vendor: {vendorName}</div>}
          {purchaseNumber && (
            <div
              onClick={(e) => { e.stopPropagation(); setTip(false); onViewPurchase && onViewPurchase(); }}
              style={{ color: '#93c5fd', cursor: 'pointer', marginTop: 4, fontWeight: 600 }}
            >
              {purchaseNumber} → View Purchase
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════
   INSERT CONNECTOR  (the hoverable + between steps)
   ══════════════════════════════════════════════════════════════════ */
const InsertConnector = ({ done, onInsert }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      className={`pps-connector ${done ? 'pps-connector--done' : ''} pps-connector--insert`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onInsert}
      title="Click to insert a step here"
    >
      {hover && (
        <div className="pps-insert-plus">+</div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════
   INSERT STEP MODAL
   ══════════════════════════════════════════════════════════════════ */
const InsertStepModal = ({ order, line, insertAfterIndex, lineSteps, onSave, onClose }) => {
  const [stage,        setStage]        = useState('');
  const [suggestions,  setSuggestions]  = useState(STAGE_SUGGESTIONS.slice(0, 6));
  const [showSug,      setShowSug]      = useState(false);
  const [isOutsourced, setIsOutsourced] = useState(false);
  const [vendors,      setVendors]      = useState([]);
  const [vendorId,     setVendorId]     = useState('');
const [description,  setDescription]  = useState('');
const [itemQty,      setItemQty]      = useState('');
const [itemUnit,     setItemUnit]     = useState('');
const [saving,       setSaving]       = useState(false);
const [matchedServiceId, setMatchedServiceId] = useState(null);
  const inputRef = useRef(null);

  // When outsourced toggled ON or stage changes, find matching service and load filtered vendors
  useEffect(() => {
    if (!isOutsourced) return;
    if (!stage.trim()) {
      // Stage not typed yet — don't load vendors at all, reset list
      setVendors([]);
      setMatchedServiceId(null);
      setVendorId('');
      return;
    }

    let cancelled = false;

    const loadVendors = async () => {
      try {
        // 1. Fetch all services and find one matching the step name exactly
        const { data: svcData } = await API.get('/vendors/services/all');
        if (cancelled) return;

        const matched = (svcData.services || []).find(
          (s) => s.name.toLowerCase() === stage.trim().toLowerCase()
        );
        const sid = matched?.id || null;
        setMatchedServiceId(sid);

        if (!sid) {
          setVendors([]);
          return;
        }

        // 2. Load vendors filtered strictly by service_id
        const { data } = await API.get('/vendors', {
          params: { limit: 200, status: 'active', service_id: sid },
        });
        if (cancelled) return;

        const list = data.vendors || [];
        setVendors(list);
        setVendorId('');
      } catch {
        toast.error('Could not load vendors');
      }
    };

    loadVendors();
    return () => { cancelled = true; };
  }, [isOutsourced, stage]);

  // Filter suggestions
  useEffect(() => {
    if (!stage.trim()) { setSuggestions(STAGE_SUGGESTIONS.slice(0, 6)); return; }
    const q = stage.toLowerCase();
    setSuggestions(STAGE_SUGGESTIONS.filter((s) => s.toLowerCase().includes(q)).slice(0, 6));
  }, [stage]);

  const position = insertAfterIndex === -1
    ? 'as first step (after Print)'
    : `after step ${insertAfterIndex + 1}`;

  const inp = {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px',
    border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14,
    outline: 'none', fontFamily: 'inherit',
  };
  const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 };

  const handleSave = async () => {
    if (!stage.trim()) { toast.error('Enter a step name'); return; }
    if (isOutsourced && !vendorId) { toast.error('Select a vendor for outsourced step'); return; }
    
    setSaving(true);
    try {
      await onSave({
        stage:       stage.trim(),
        isOutsourced,
        vendorId:    isOutsourced ? Number(vendorId) : null,
        vendorName:  isOutsourced ? (vendors.find((v) => String(v.id) === String(vendorId))?.company_name || '') : null,
        qty:         isOutsourced ? (Number(itemQty) || Number(line.qty) || 1) : (Number(line.qty) || 1),
        unit:        isOutsourced ? (itemUnit.trim() || line.unit || 'Nos') : (line.unit || 'Nos'),
        description: isOutsourced ? description.trim() : '',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: 440, maxWidth: '95vw', boxShadow: '0 8px 40px rgba(0,0,0,0.18)', maxHeight: '90vh', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Insert Step</div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
              {line.product} · {line.qty} {line.unit} · {position}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#9ca3af', lineHeight: 1, padding: 0 }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Step name with autocomplete */}
          <div style={{ position: 'relative' }}>
            <label style={lbl}>Step Name *</label>
            <input
              ref={inputRef}
              style={inp}
              autoFocus
              value={stage}
              placeholder="e.g. Lamination, Quality check…"
              onChange={(e) => { setStage(e.target.value); setShowSug(true); }}
              onFocus={() => setShowSug(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter')  handleSave();
                if (e.key === 'Escape') onClose();
              }}
            />
            {showSug && suggestions.length > 0 && stage && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2,
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                boxShadow: '0 4px 16px rgba(0,0,0,0.1)', zIndex: 10,
                maxHeight: 180, overflowY: 'auto',
              }}>
                {suggestions.map((s) => (
                  <div
                    key={s}
                    onMouseDown={(e) => { e.preventDefault(); setStage(s); setShowSug(false); }}
                    style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.background = ''}
                  >{s}</div>
                ))}
              </div>
            )}
          </div>

          {/* Inhouse / Outsource toggle */}
          <div style={{ display: 'flex', gap: 10 }}>
            {[
              { val: false, icon: '🏭', label: 'In-house',   sub: 'Done internally' },
              { val: true,  icon: '🔗', label: 'Outsourced', sub: 'Send to vendor' },
            ].map((opt) => (
              <label
                key={String(opt.val)}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', cursor: 'pointer',
                  border: `2px solid ${isOutsourced === opt.val ? '#6366f1' : '#e5e7eb'}`,
                  borderRadius: 10,
                  background: isOutsourced === opt.val ? '#eef2ff' : '#fff',
                }}
              >
                <input
                  type="radio" name="stepType"
                  checked={isOutsourced === opt.val}
                  onChange={() => setIsOutsourced(opt.val)}
                  style={{ accentColor: '#6366f1' }}
                />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{opt.icon} {opt.label}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>{opt.sub}</div>
                </div>
              </label>
            ))}
          </div>

          {/* Outsource vendor field */}
          {isOutsourced && (
            <div style={{ background: '#faf5ff', border: '1px solid #ede9fe', borderRadius: 10, padding: 14 }}>
              <label style={lbl}>Vendor *</label>
              <select
                style={{ ...inp, background: '#fff', cursor: 'pointer' }}
                value={vendorId}
                onChange={(e) => setVendorId(e.target.value)}
              >
                <option value="">— Select vendor —</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>{v.company_name} ({v.vendor_code})</option>
              ))}
              </select>
              <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Qty *</label>
                  <input
                    type="number" min="0.01" step="any"
                    style={inp}
                    placeholder={String(line.qty || '')}
                    value={itemQty}
                    onChange={(e) => setItemQty(e.target.value)}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Unit</label>
                  <select
                    style={{ ...inp, background: '#fff', cursor: 'pointer' }}
                    value={itemUnit || line.unit || 'Nos'}
                    onChange={(e) => setItemUnit(e.target.value)}
                  >
                    {['Nos', 'Pcs', 'Sheets', 'Reams', 'Kg', 'Grams', 'Meters', 'Feet', 'Inches', 'Box', 'Set', 'Pair'].map(u => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                <label style={lbl}>Description</label>
                <input
                  style={inp}
                  placeholder="e.g. Lamination for visiting card…"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
                A DRAFT purchase will be created automatically for this vendor linked to order {order.orderNumber}. Add cost in the Purchase module.
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{ padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 14 }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ padding: '8px 18px', border: 'none', borderRadius: 8, background: '#6366f1', color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, opacity: saving ? 0.7 : 1 }}
          >
            {saving ? 'Saving…' : 'Insert Step'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════
   CONFIRM POPUP — mark step done
   ══════════════════════════════════════════════════════════════════ */
const ConfirmPopup = ({ stepLabel, isOutsourced, vendorName, onConfirm, onCancel }) => (
  <div className="pps-overlay" onClick={onCancel}>
    <div className="pps-popup" onClick={(e) => e.stopPropagation()}>
      <div className="pps-popup-icon">✅</div>
      <div className="pps-popup-title">Mark step as done?</div>
      <div className="pps-popup-body">
        <strong>"{stepLabel}"</strong><br />
        {isOutsourced && vendorName && (
          <span style={{ fontSize: 13, color: '#7c3aed' }}>🔗 Outsourced to {vendorName}<br /></span>
        )}
        Is this production step completed?
      </div>
      <div className="pps-popup-actions">
        <button className="pps-btn pps-btn--cancel" onClick={onCancel}>No, not yet</button>
        <button className="pps-btn pps-btn--confirm" onClick={onConfirm}>Yes, done ✓</button>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════
   DELIVERY INLINE FORM — record ready qty (shown inside drawer, no popup)
   ══════════════════════════════════════════════════════════════════ */
const DeliveryInlineForm = ({ line, remainingQty, onConfirm, onCancel }) => {
  const [qty,  setQty]  = useState(remainingQty);
  const [note, setNote] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const inp = {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px',
    border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14,
    outline: 'none', background: '#fff',
  };
  const lbl = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#374151' };

  const submit = () => {
    const n = Number(qty);
    if (!n || n <= 0)     { toast.error('Enter a valid quantity'); return; }
    if (n > remainingQty) { toast.error(`Max ${remainingQty}`); return; }
    onConfirm({ qty: n, date, note });
  };

  return (
    <div style={{
      marginTop: 16,
      background: '#f0fdf4',
      border: '1.5px solid #bbf7d0',
      borderRadius: 12,
      padding: '16px 18px',
    }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: '#166534', marginBottom: 2 }}>
        ✅ Quantity Ready
      </div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>
        {line.product || '(unnamed)'} · Remaining: <strong>{remainingQty} {line.unit}</strong>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={lbl}>Qty Ready Now *</label>
          <input
            type="number" min={1} max={remainingQty}
            value={qty} onChange={(e) => setQty(e.target.value)}
            style={inp}
            autoFocus
          />
        </div>
        <div>
          <label style={lbl}>Ready Date *</label>
          <input
            type="date" value={date}
            onChange={(e) => setDate(e.target.value)}
            style={inp}
            onClick={(e) => e.target.showPicker && e.target.showPicker()}
          />
        </div>
        <div>
          <label style={lbl}>Note (optional)</label>
          <input
            type="text" value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Sent via courier"
            style={inp}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
        <button
          onClick={onCancel}
          style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
        >
          Cancel
        </button>
        <button
          onClick={submit}
          style={{ padding: '8px 18px', border: 'none', borderRadius: 8, background: '#16a34a', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
        >
          Ready ✓
        </button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════ */
const ProductProgressStrip = ({ order, onOrderUpdate }) => {
  const navigate = useNavigate();
  const { log }  = useStaffLog();
  const [popup,  setPopup]  = useState(null);
  const [saving, setSaving] = useState(false);

  const items          = safeArray(order.items);
  const processEntries = safeArray(order.processEntries);
  const readyEntries   = safeArray(order.readyEntries);
  const isDelivered    = order.status === 'delivered';
  const stepsCompleted = order.stepsCompleted || {};

  /* ── Persist processEntries ── */
  const persistEntries = useCallback(async (updated) => {
    const { data } = await API.put(`/sales-orders/${order.id}`, { processEntries: updated });
    onOrderUpdate && onOrderUpdate(data.order || { ...order, processEntries: updated });
  }, [order, onOrderUpdate]);

  /* ── Mark step done ── */
  const markStepDone = async (entryId) => {
    setSaving(true);
    try {
      const updated = { ...stepsCompleted, [entryId]: true };
      await API.put(`/sales-orders/${order.id}`, { stepsCompleted: updated });
      onOrderUpdate && onOrderUpdate({ ...order, stepsCompleted: updated });
      toast.success('Step marked as done');
      await log('production_step_done', `Marked step done for order #${order.orderNumber}`, 'SalesOrder', order.id);
    } catch {
      toast.error('Could not save');
    } finally {
      setSaving(false);
      setPopup(null);
    }
  };

  /* ── Record ready qty ── */
  const recordReady = async ({ qty, date, note }, lineId) => {
    setSaving(true);
    try {
      const entry      = { id: Date.now(), itemLineId: lineId, qty: Number(qty), date, note: note || '' };
      const updated    = [...readyEntries, entry];
      const { data }   = await API.put(`/sales-orders/${order.id}`, { readyEntries: updated });
      onOrderUpdate && onOrderUpdate(data.order || { ...order, readyEntries: updated });
      const line       = items.find((i) => i.id === lineId);
      const totalReady = updated.filter((e) => e.itemLineId === lineId).reduce((s, e) => s + Number(e.qty), 0);
      const ordQty     = Number(line?.qty) || 0;
      toast.success(totalReady >= ordQty
        ? `${line?.product || 'Item'} fully ready ✓`
        : `${qty} ${line?.unit || ''} marked ready — ${ordQty - totalReady} remaining`
      );
    } catch {
      toast.error('Could not save');
    } finally {
      setSaving(false);
      setPopup(null);
    }
  };

  /* ════════════════════════════════════════════════════════════════
     INSERT STEP
     ════════════════════════════════════════════════════════════════
     insertAfterIndex: position within lineSteps (-1 = before first)
     For outsourced steps: auto-create a DRAFT purchase first.
     If purchase creation fails → rollback, do not save step.
  */
  const handleInsertStep = async ({ stage, isOutsourced, vendorId, vendorName, qty, unit, description }, lineId, insertAfterIndex, lineSteps) => {
    setSaving(true);

    let purchaseId     = null;
    let purchaseNumber = null;

    try {
      /* 1. Auto-create or reuse purchase if outsourced */
      if (isOutsourced) {
        const today = new Date().toISOString().split('T')[0];

        // Check if a purchase already exists for this vendor + order
        const { data: existing } = await API.get('/purchases', {
          params: { vendor_id: vendorId, order_id: order.id, limit: 5 },
        });

        const activePurchase = (existing.purchases || []).find(
          (p) => p.status === 'DRAFT' || p.status === 'CONFIRMED'
        );

        if (activePurchase && activePurchase.status === 'CONFIRMED') {
          // Block — user must add manually
          toast.error(
            `A confirmed purchase ${activePurchase.purchase_number} already exists for this vendor on order ${order.orderNumber}. Add the item there manually.`,
            { duration: 7000 }
          );
          setSaving(false);
          return;
        }

        if (activePurchase && activePurchase.status === 'DRAFT') {
          // Reuse — fetch full purchase detail first to get all existing items
          const { data: fullPur } = await API.get(`/purchases/${activePurchase.id}`);
          const existingItems = fullPur.purchase.items || [];
          const { data: purData } = await API.put(`/purchases/${activePurchase.id}`, {
            items: [
              ...existingItems.map((i) => ({
                item_name:       i.item_name,
                description:     i.description,
                quantity:        Number(i.quantity),
                unit:            i.unit,
                rate:            Number(i.rate),
                discount_amount: Number(i.discount_amount),
                tax_rate:        Number(i.tax_rate),
              })),
              {
                item_name:       stage,
                description:     description || `${order.orderNumber} — ${order.customerName || ''}`,
                quantity:        qty,
                unit:            unit,
                rate:            0,
                discount_amount: 0,
                tax_rate:        0,
              },
            ],
          });
          purchaseId     = purData.purchase.id;
          purchaseNumber = purData.purchase.purchase_number;
        } else {
          // Create new purchase
          const { data: purData } = await API.post('/purchases', {
            vendor_id:     vendorId,
            order_id:      order.id,
            purchase_type: 'OUTSOURCING',
            purchase_date: today,
            tax_type:      'INTRA_STATE',
            other_charges: 0,
            round_off:     0,
            notes:         `Auto-created from production step "${stage}" on order ${order.orderNumber}`,
            items: [{
              item_name:       stage,
              description:     description || `${order.orderNumber} — ${order.customerName || ''}`,
              quantity:        qty,
              unit:            unit,
              rate:            0,
              discount_amount: 0,
              tax_rate:        0,
            }],
          });
          purchaseId     = purData.purchase.id;
          purchaseNumber = purData.purchase.purchase_number;
        }
      }

      /* 2. Build the new step entry */
      const newEntry = {
        id:                `PE${Date.now()}`,
        productLineId:     lineId,
        stage,
        isOutsourced,
        outsourceVendorId:   isOutsourced ? vendorId       : null,
        outsourceVendorName: isOutsourced ? vendorName     : null,
        outsourcePurchaseId: purchaseId,
        outsourcePurchaseNumber: purchaseNumber,
        createdAt:           new Date().toISOString(),
      };

      /* 3. Splice into the correct position within lineSteps */
      const updated = [...lineSteps];
      updated.splice(insertAfterIndex + 1, 0, newEntry);

      /* 4. Rebuild full processEntries — preserve other lines' entries */
      const others      = processEntries.filter((e) => e.productLineId !== lineId);
      const newEntries  = [...others, ...updated];

      /* 5. Persist — if this fails, rollback purchase by cancelling it */
      try {
        await persistEntries(newEntries);
      } catch (saveErr) {
        // Roll back the purchase we just created
        if (purchaseId) {
          try { await API.post(`/purchases/${purchaseId}/cancel`); } catch {}
        }
        throw saveErr;
      }

      toast.success(isOutsourced
        ? `Step inserted · Purchase ${purchaseNumber} created (DRAFT)`
        : 'Step inserted'
      );
      setPopup(null);

    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not insert step — rolled back');
    } finally {
      setSaving(false);
    }
  };

  /* ════════════════════════════════════════════════════════════════
     REMOVE STEP
     Block if the step has a linked purchase that is not cancelled.
  ════════════════════════════════════════════════════════════════ */
  const handleRemoveStep = async (entry) => {
    /* Check linked purchase */
    if (entry.outsourcePurchaseId) {
      try {
        const { data } = await API.get(`/purchases/${entry.outsourcePurchaseId}`);
        const pur = data.purchase;
        if (pur && pur.status !== 'CANCELLED') {
          toast.error(
            `Cannot remove — linked purchase ${pur.purchase_number} is ${pur.status}. ` +
            `Cancel the purchase first, then remove this step.`,
            { duration: 6000 }
          );
          return;
        }
      } catch {
        // Purchase not found — safe to delete step
      }
    }

    if (!window.confirm(`Remove step "${entry.stage}"?`)) return;

    setSaving(true);
    try {
      const updated = processEntries.filter((e) => e.id !== entry.id);
      await persistEntries(updated);
      toast.success('Step removed');
      await log('production_step_removed', `Removed production step from order #${order.orderNumber}`, 'SalesOrder', order.id);
    } catch {
      toast.error('Could not remove step');
    } finally {
      setSaving(false);
    }
  };

  if (items.length === 0) return null;

  return (
    <div className="pps-root">
      <div className="pps-heading">
        🚦 Production Progress
        {saving && <span className="pps-saving">saving…</span>}
      </div>

      {items.map((line) => {
        const lineId         = line.id;
        const lineSteps      = processEntries.filter((e) => e.productLineId === lineId);
        const orderedQty     = Number(line.qty) || 0;
        const ready          = readyQtyForLine(readyEntries, lineId);
        const remaining      = Math.max(0, orderedQty - ready);
        const itemFullyReady = isDelivered || ready >= orderedQty;

        const readyLabel = ready > 0 && !itemFullyReady
          ? `Ready (${ready}/${orderedQty})`
          : 'Ready';

        /* Full steps array: Print (fixed) → mid-steps → Ready (fixed) */
        const steps = [
          { id: '__print', label: 'Print', done: true,  isFixed: true },
          ...lineSteps.map((s, midIdx) => ({
            id:                     s.id,
            label:                  s.stage,
            done:                   !!stepsCompleted[s.id],
            isFixed:                false,
            midIdx,
            isOutsourced:           s.isOutsourced || false,
            outsourceVendorName:    s.outsourceVendorName || null,
            outsourcePurchaseId:    s.outsourcePurchaseId || null,
            outsourcePurchaseNumber: s.outsourcePurchaseNumber || null,
            _entry:                 s,
          })),
          { id: '__ready', label: readyLabel, done: itemFullyReady, isFixed: true, isReadyStep: true },
        ];

        const firstPendingIdx = steps.findIndex((s) => !s.done);

        return (
          <div key={lineId} className="pps-product-block">
            {/* Product header */}
            <div className="pps-product-name">
              {line.product || '(unnamed)'}
              <span className="pps-product-qty"> — {line.qty} {line.unit}</span>
              {ready > 0 && !itemFullyReady && (
                <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#d97706', borderRadius: 20, padding: '2px 8px' }}>
                  {ready}/{orderedQty} ready
                </span>
              )}
              {itemFullyReady && (
                <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 700, background: '#d1fae5', color: '#059669', borderRadius: 20, padding: '2px 8px' }}>
                  Fully Ready ✓
                </span>
              )}
            </div>

            {/* Track */}
            <div className="pps-track">
              {steps.map((step, idx) => {
                const isActive  = idx === firstPendingIdx;
                const isDone    = step.done;
                const prevDone  = idx === 0 || steps[idx - 1].done;
                const clickable = (!isDone && prevDone)
                  || (step.isReadyStep && ready > 0 && !itemFullyReady && prevDone);

                /*
                  insertAfterIndex for the connector AFTER this step:
                  - After Print (idx=0):        insertAfterIndex = -1  (before lineSteps[0])
                  - After mid-step midIdx:       insertAfterIndex = midIdx
                  - Don't show insert after Ready (it's the last)
                */
                const insertAfterIndex = step.id === '__print' ? -1 : step.midIdx;

                return (
                  <React.Fragment key={step.id}>
                    <StepCircle
                      index={idx}
                      done={isDone}
                      active={isActive}
                      label={step.label}
                      isOutsourced={step.isOutsourced}
                      vendorName={step.outsourceVendorName}
                      purchaseNumber={step.outsourcePurchaseNumber}
                      clickable={!!clickable}
                      onViewPurchase={step.outsourcePurchaseId
                        ? () => navigate(`/admin/purchases/${step.outsourcePurchaseId}`)
                        : null
                      }
                      onClick={() => {
                        if (step.isReadyStep) {
                          setPopup({ type: 'delivery', lineId, line, remainingQty: remaining });
                        } else if (!step.isFixed) {
                          setPopup({ type: 'step', stepId: step.id, label: step.label, isOutsourced: step.isOutsourced, vendorName: step.outsourceVendorName, entry: step._entry });
                        }
                      }}
                    />

                    {/* Connector — clickable to insert, not after Ready */}
                    {idx < steps.length - 1 && !step.isReadyStep && (
                      <InsertConnector
                        done={isDone}
                        onInsert={() => setPopup({
                          type: 'insert',
                          lineId,
                          line,
                          lineSteps,
                          insertAfterIndex,
                        })}
                      />
                    )}
                    {/* Plain connector before Ready (no insert after last mid-step → Ready gap) */}
                    {idx < steps.length - 1 && step.isReadyStep && (
                      <div className={`pps-connector ${isDone ? 'pps-connector--done' : ''}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Step list below track — shows outsource info + remove button */}
            {lineSteps.length > 0 && (
              <div className="pps-step-list">
                {lineSteps.map((s, idx) => (
                  <div key={s.id} className="pps-step-row">
                    <span className="pps-step-num">{idx + 1}</span>
                    <span className="pps-step-name">{s.stage}</span>
                    {s.isOutsourced && s.outsourceVendorName && (
                      <span className="pps-step-vendor">🔗 {s.outsourceVendorName}</span>
                    )}
                    {s.outsourcePurchaseNumber && (
                      <span
                        className="pps-step-purlink"
                        onClick={() => navigate(`/admin/purchases/${s.outsourcePurchaseId}`)}
                        title="View purchase"
                      >
                        {s.outsourcePurchaseNumber}
                      </span>
                    )}
                    {stepsCompleted[s.id] && (
                      <span className="pps-step-done-badge">Done ✓</span>
                    )}
                    <button
                      className="pps-step-remove"
                      title="Remove step"
                      onClick={() => handleRemoveStep(s)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Inline delivery form — shown below this product's track when Ready is clicked */}
            {popup?.type === 'delivery' && popup.lineId === lineId && (
              <DeliveryInlineForm
                line={popup.line}
                remainingQty={popup.remainingQty}
                onConfirm={(data) => recordReady(data, popup.lineId)}
                onCancel={() => setPopup(null)}
              />
            )}
          </div>
        );
      })}

      {/* Mark step done popup */}
      {popup?.type === 'step' && (
        <ConfirmPopup
          stepLabel={popup.label}
          isOutsourced={popup.isOutsourced}
          vendorName={popup.vendorName}
          onConfirm={() => markStepDone(popup.stepId)}
          onCancel={() => setPopup(null)}
        />
      )}

      {/* Ready qty form is now rendered inline inside each product block above */}

      {/* Insert step modal */}
      {popup?.type === 'insert' && (
        <InsertStepModal
          order={order}
          line={popup.line}
          insertAfterIndex={popup.insertAfterIndex}
          lineSteps={popup.lineSteps}
          onSave={(stepData) => handleInsertStep(stepData, popup.lineId, popup.insertAfterIndex, popup.lineSteps)}
          onClose={() => setPopup(null)}
        />
      )}
    </div>
  );
};

export default ProductProgressStrip;